'use client';

import { useState, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { useModalStore } from '@/src/shared/store/modal.store';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { OrderCard, type OrderCardProps } from '@/src/entities/order/ui/OrderCard';
import { cn } from '@/src/shared/lib/utils';

type SortOption = 'all' | 'date-new' | 'date-old';

interface ClientOrdersListProps {
  orders: OrderCardProps[];
  categoryName?: string;
  subcategoryName?: string;
  initialDisplayCount?: number;
  className?: string;
  /**
   * Callback when worker clicks respond button on an order
   */
  onRespond?: (orderId: string) => void;
  /**
   * Callback when order card is clicked (for viewing details)
   */
  onClick?: (orderId: string) => void;
  /**
   * Callback when owner clicks "Renew" on their own order (isMyOrder=true)
   */
  onRenew?: (orderId: string) => void;
}

export const ClientOrdersList = ({
  orders,
  categoryName,
  subcategoryName,
  initialDisplayCount = 4,
  className,
  onRespond,
  onClick,
  onRenew,
}: ClientOrdersListProps) => {
  const t = useTranslations('clientOrders');
  const [displayCount, setDisplayCount] = useState(initialDisplayCount);
  const [sortBy, setSortBy] = useState<SortOption>('all');
  const openModal = useModalStore((state) => state.openModal);

  const handleLocationClick = useCallback(
    (orderId: string) => {
      const order = orders.find((o) => o.id === orderId);
      if (!order?.latitude || !order?.longitude || order.latitude === 0) {
        toast.info('Location not available for this order.');
        return;
      }
      openModal('order-location', {
        latitude: order.latitude,
        longitude: order.longitude,
        locationLabel: order.location,
      });
    },
    [orders, openModal],
  );

  // Sort orders based on selected option
  const sortedOrders = [...orders].sort((a, b) => {
    switch (sortBy) {
      case 'date-new': {
        const dateA = new Date(a.startDate);
        const dateB = new Date(b.startDate);
        return dateB.getTime() - dateA.getTime();
      }
      case 'date-old': {
        const dateA = new Date(a.startDate);
        const dateB = new Date(b.startDate);
        return dateA.getTime() - dateB.getTime();
      }
      default:
        return 0;
    }
  });

  const displayedOrders = sortedOrders.slice(0, displayCount);
  const hasMore = displayCount < orders.length;

  const handleLoadMore = () => {
    setDisplayCount((prev) => Math.min(prev + 4, orders.length));
  };

  return (
    <div className={cn('w-full', className)}>
      {/* Header with Sort */}
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <Typography variant="h2" weight="bold" color="primary">
          {t('title')}
        </Typography>

        {/* Sort Dropdown */}
        <div className="flex items-center gap-2">
          <Typography variant="body" color="secondary">
            {t('sortBy')}
          </Typography>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className={cn(
              'px-4 py-2 rounded-full border-2 border-border-primary',
              'bg-background-primary text-text-primary',
              'focus:outline-none focus:border-accent-primary',
              'cursor-pointer transition-colors'
            )}
          >
            <option value="all">{t('sortAll')}</option>
            <option value="date-new">{t('sortNewest')}</option>
            <option value="date-old">{t('sortOldest')}</option>
          </select>
        </div>
      </div>

      {/* Orders Grid */}
      {displayedOrders.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          {displayedOrders.map((order) => (
            <OrderCard
              key={order.id}
              {...order}
              onRespond={onRespond}
              onClick={onClick}
              onLocationClick={handleLocationClick}
              onRenew={onRenew}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Typography variant="h3" color="secondary">
            {t('noOrders')}
          </Typography>
          <Typography variant="body" color="secondary" className="mt-2">
            {t('noOrdersMessage')}
          </Typography>
        </div>
      )}

      {/* Load More Button */}
      {hasMore && (
        <div className="bg-surface-highlight py-4 rounded-[16px] flex items-center justify-center">
          <button
            onClick={handleLoadMore}
            className={cn(
              'flex items-center gap-2 text-accent-primary font-semibold',
              'hover:text-accent-secondary transition-colors',
              'focus:outline-none focus:underline'
            )}
          >
            <span>{t('loadMore')}</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              className="transition-transform group-hover:translate-y-0.5"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};
